import {WidgetConfig} from "../../../types/config";
import UserData from "../../user/UserData";
import ComponentConfig from "../../../config/componentConfig";
import {ChatMessageHandler} from "../chatMessageHandler";
import {ChatEventHandler} from "../chatEventHandler";
import {InvalidChatSessionError} from "../../../types/errors";
import {ComponentInterface} from "../../../types/chat";
import {ErrorTypes} from "../../../types/enums";
import {ClientErrorLogger} from "../../utils/clientErrorLogger";

export default abstract class AbstractChatConnector
{
    private readonly MAX_RETRIES: number = 5;
    private readonly RETRY_TIMEOUTS: number[] = [0, 500, 2000, 5000, 10000];

    private chatWebSocket: null|WebSocket = null;
    private connectionInProgress: boolean = false;
    private connectionInError: boolean = false;
    private userData: null|UserData = null;

    private connectionRetryCount: number = 0;
    private lastConnectionError: any = null;

    public intentionalDisconnect: boolean = false;

    constructor(
        protected config: WidgetConfig,
        protected componentConfig: ComponentConfig,
        private wssUrl: string,
        protected messageHandler: ChatMessageHandler,
        protected eventHandler: ChatEventHandler,
        protected alpineComponent: ComponentInterface,
    ) {
    }

    protected abstract registerOnMessageHandler( webSocket: WebSocket ):Promise<void>;
    public abstract getChatType( ): string;

    public async initConnection(resetReconnectCounter:boolean = false): Promise<void> {
        if (this.isWebsocketOpen()) {
            return;
        }

        if (!this.connectionInProgress) {
            this.connectionInError = false;
            this.connectionInProgress = true;
            if (resetReconnectCounter) {
                this.connectionRetryCount = 0;
            }

            await this.initConnectionWithRetry();
        }

        await this.waitForFinishedInitConnection();
    }

    public isWebsocketOpen(): boolean {
        return this.chatWebSocket !== null && this.chatWebSocket.readyState === WebSocket.OPEN;
    }

    private async initConnectionWithRetry(): Promise<void>
    {
        if (this.lastConnectionError instanceof InvalidChatSessionError) {
            await this.finishInitConnectionWithError();
            return;
        }

        if (this.connectionRetryCount > this.MAX_RETRIES) {
            this.alpineComponent.handleInvalidChatSessionError(ErrorTypes.INIT_CONNECTION_ERROR);
            await this.finishInitConnectionWithError();
            return;
        }

        await this.waitBeforeRetry(this.RETRY_TIMEOUTS, this.connectionRetryCount);
        this.connectionRetryCount++;

        try {
            await this.prepareWebSocketIfClosed();
            await this.initWebsocketConnection();
        } catch (error) {
            this.lastConnectionError = error;
            this.logInitConnectionError(error);

            if (error instanceof InvalidChatSessionError) {
                this.alpineComponent.handleInvalidChatSessionError(ErrorTypes.INVALID_CHAT_SESSION);
                await this.finishInitConnectionWithError();
                return;
            }

            await this.initConnectionWithRetry();
        }
    }

    private async finishInitConnection(): Promise<void> {
        this.connectionInError = false;
        this.connectionRetryCount = 0;
        this.lastConnectionError = null;
        this.connectionInProgress = false;
        this.alpineComponent.resetSystemError(ErrorTypes.INIT_CONNECTION_ERROR);
    }

    private async finishInitConnectionWithError(): Promise<void> {
        this.connectionInError = true;
        // this.connectionRetryCount = 0;
        this.connectionInProgress = false;
    }

    private async prepareWebSocketIfClosed(): Promise<void> {
        if (this.chatWebSocket &&
            (this.chatWebSocket.readyState === WebSocket.CLOSING || this.chatWebSocket.readyState === WebSocket.CLOSED)) {
            await this.closeWebsocketConnection();
        }
    }

    private async waitBeforeRetry(retryTimeouts: number[], retryCount: number): Promise<void> {
        let timeout = retryTimeouts[Math.min(retryCount, retryTimeouts.length - 1)];
        await new Promise(resolve => setTimeout(resolve, timeout));
    }

    private logInitConnectionError(error: any): void {
        const context: any = {};
        context.reason = 'initConnection error';
        context.messageType = this.getChatType();
        if (this.chatWebSocket) {
            context.websocket = this.chatWebSocket;
        }
        (new ClientErrorLogger()).logError(error, context);
    }

    public async getWebSocket():Promise<WebSocket|null> {
        if (this.chatWebSocket !== null && this.isWebsocketOpen()) {
            return this.chatWebSocket;
        }
        await this.initConnection();
        if (this.chatWebSocket !== null && this.isWebsocketOpen()) {
            return this.chatWebSocket;
        }
        return null;
    }

    private async waitForFinishedInitConnection(): Promise<void> {
        return new Promise<void>((resolve) => {
            const check = () => {
                if (this.isValidFinishedInitConnectionState()) {
                    resolve();
                } else {
                    setTimeout(check, 100);
                }
            };
            check();
        });
    }

    protected isValidFinishedInitConnectionState(): boolean {

        if (this.connectionInError) {
            return true;
        }

        if (this.chatWebSocket === null) {
            return false;
        }

        if (this.chatWebSocket.readyState !== WebSocket.OPEN) {
            return false;
        }

        if(this.connectionInProgress) {
            return false;
        }

        return true;
    }

    private async waitForWebsocketState( state:number ): Promise<void> {
        return new Promise<void>((resolve) => {
            const check = () => {
                let websocket = this.chatWebSocket;
                let websocketState = websocket ? websocket.readyState : null;
                if (state === websocketState) {
                    resolve();
                } else {
                    setTimeout(check, 100);
                }
            };
            check();
        });
    }

    public stopTimer(): void {
    }

    public async closeConnection():Promise<void> {
        this.intentionalDisconnect = true;

        this.stopTimer();
        if(this.isWebsocketOpen()) {
            await this.closeWebsocketConnection();
        }

        if(this.chatWebSocket === null ||
            this.chatWebSocket?.readyState === WebSocket.CLOSING ||
            this.chatWebSocket?.readyState === WebSocket.CLOSED) {
            await this.closeChatSessionId();
        }
        this.chatWebSocket = null;
        this.intentionalDisconnect = false;
    }

    public isWebsocketAvailable():boolean {
        if (this.chatWebSocket === null) {
            return false;
        }

        let websocketState = this.chatWebSocket.readyState;
        return websocketState === WebSocket.OPEN;
    }

    protected async specialConnectionInitialisation( ):Promise<void>
    {

    }

    private async initWebsocketConnection():Promise<void>
    {
        let token = await this.getChatLoginToken();

        if (token === "") {
            throw new InvalidChatSessionError('token is empty');
        }

        let websocketPath = this.wssUrl + '?token='+token;
        let webSocket = new WebSocket(websocketPath);
        webSocket.onerror = () => {
            (new ClientErrorLogger()).logInfo(
                'Websocket connection failed',
                'info',
                {
                    reason: 'Websocket connection failed',
                    messageType: this.getChatType(),
                    websocket: webSocket
                }
            );
            this.initConnectionWithRetry();
        };

        webSocket.onclose = (event) => {
            if (!this.intentionalDisconnect
                && this.getChatType() === 'agent'
                && !this.connectionInProgress
            ) {
                this.initConnection();
            }
        };
        webSocket.onopen = () => {
            this.specialConnectionInitialisation();
            this.finishInitConnection();
        };

        await this.registerOnMessageHandler(webSocket);
        this.chatWebSocket = webSocket;
    }

    private async getChatLoginToken():Promise<string>
    {
        const url = "/chat/loginToken";
        const body = JSON.stringify({
            csrf: this.getCurrentCsrf(),
            chatType: this.getChatType(),
            entryPoint: this.componentConfig.entryPoint,
            entryPointPath: this.componentConfig.entryPointPath,
            queue: this.componentConfig.queue,
        })

        const response = await this.fetchResponse(url, body);

        if (!response.ok || response.status !== 200) {
            throw new InvalidChatSessionError(response.statusText);
        }

        const json = await response.json();
        this.updateChatData(json.csrf, json.agentChatId, json.botChatId);
        let userChatSessionId = json.userSessionId ?? "";

        if(json.customerFirstName === '' && json.customerLastName === '' && this.userData instanceof UserData) {
            this.userData.setUserSessionId(json.sid);
            this.userData.setBotChatSessionId(json.botChatId);
            this.userData.setUserChatSessionId(userChatSessionId);
        } else {
            this.userData = new UserData(
                json.customerFirstName,
                json.customerLastName,
                json.subscriberId,
                json.sid,
                userChatSessionId,
                '',
                json.botChatId
            );
        }

        return encodeURIComponent(json.token);
    }

    private getCurrentCsrf():string
    {
        let csrf = '';
        document.querySelectorAll('div.chat-data-container').forEach(element => {
            let data = element.getAttribute('data-csrf');
            if (data) {
                csrf = data;
            }
        });

        return csrf;
    }

    private updateChatData(newToken:string|null, agentChatId: string|null, botChatId: string|null):void
    {
        this.componentConfig.setAgentChatId(agentChatId);
        this.componentConfig.setBotChatId(botChatId);

        document.querySelectorAll('div.chat-data-container').forEach(element => {
            if(newToken !== null) {
                if (element.hasAttribute('data-csrf')) {
                    element.setAttribute('data-csrf', newToken)
                }
            }
            if (element.hasAttribute('data-agentchat')) {
                let updatedAgentChatId = agentChatId === null ? '' : agentChatId;
                element.setAttribute('data-agentchat', updatedAgentChatId)
            }
            if (element.hasAttribute('data-botchat')) {
                let updatedBotChatId = botChatId === null ? '' :botChatId;
                element.setAttribute('data-botchat', updatedBotChatId)
            }
        });
    }

    private async closeChatSessionId():Promise<void>
    {
        const url = "/chat/close";
        this.updateChatData(null, null, null);

        const response = await this.fetchResponse(url);
        if (!response.ok) {
            (new ClientErrorLogger()).logError('Close-Action fehlgeschlagen', {
                reason: response.statusText
            });
        }
    }

    public async minimizeChat():Promise<void>
    {
        const url = "/chat/minimize";

        const response = await this.fetchResponse(url);
        if (!response.ok) {
            (new ClientErrorLogger()).logError('Minimize fehlgeschlagen', {
                reason: response.statusText
            });
        }
    }

    private async fetchResponse(url: string, body: string|null = null): Promise<Response>
    {
        return fetch(url, {
            method: 'POST',
            body: body,
            mode: 'cors',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            },
            redirect: 'follow',
        });
    }

    public async closeWebsocketConnection():Promise<void>
    {
        if (this.chatWebSocket === null){
            return;
        }

        if (this.chatWebSocket && (this.chatWebSocket.readyState === WebSocket.OPEN || this.chatWebSocket.readyState === WebSocket.CONNECTING)) {
            this.chatWebSocket.close();
        }

        await this.waitForWebsocketState(WebSocket.CLOSED);

        this.chatWebSocket = null;
    }

    public getUserData(): UserData | null
    {
        return this.userData;
    }

    public setUserData(userData: UserData): void
    {
        this.userData = userData;
    }
}